import { THREE_DS_IFRAME_ZINDEX } from "common/constants";
import { SupportedLocale } from "common/locale/labels";

import { isMobile } from "./browser";

const I_FRAME_NAME = "three-ds-i-frame-redirect-page-wrapper";

const ThreeDsModalSizes = {
    WIDE: { width: 600, height: 400 },
    TALL: { width: 500, height: 600 },
};

export const createIFrame = ({
    iFrameSize,
    parent = document.body,
}: {
    iFrameSize?: string | { width?: string; height?: string; top?: string; left?: string };
    parent?: HTMLElement;
} = {}) => {
    const defaultSizes = !isMobile()
        ? {
              width: `${ThreeDsModalSizes.TALL.width}px`,
              height: `${ThreeDsModalSizes.TALL.height}px`,
              top: "130px",
              left: `calc(50% - ${ThreeDsModalSizes.TALL.width / 2}px)`,
          }
        : { width: "100%", height: "100%", top: "0px", left: "0px" };

    const size = !iFrameSize
        ? defaultSizes
        : typeof iFrameSize === "string"
        ? { ...defaultSizes, width: iFrameSize, height: iFrameSize }
        : { ...defaultSizes, ...iFrameSize };

    const { height: parentHeight, width: parentWidth } = parent?.getBoundingClientRect() || {};
    const isIFrameBiggerThanScreen =
        (!!parentWidth && size.width.includes("px") && parentWidth < Number(size.width.replace("px", ""))) ||
        (!!parentHeight && size.height.includes("px") && parentHeight < Number(size.height.replace("px", "")));

    // Add iFrame to load redirect
    const iFrame = document.createElement("iframe");
    iFrame.name = I_FRAME_NAME;
    iFrame.style.width = isIFrameBiggerThanScreen ? "100%" : size.width;
    iFrame.style.height = isIFrameBiggerThanScreen ? "100vh" : size.height;
    iFrame.style.top = isIFrameBiggerThanScreen ? "0%" : size.top;
    iFrame.style.left = isIFrameBiggerThanScreen ? "0%" : size.left;
    iFrame.style.position = "absolute";
    iFrame.style.background = "white";
    iFrame.style.zIndex = `${THREE_DS_IFRAME_ZINDEX}`;
    iFrame.style.border = "0";
    iFrame.frameBorder = "0";

    if (parent) {
        parent.appendChild(iFrame);
    }

    return iFrame;
};

export const redirect = (
    iFrame: HTMLIFrameElement,
    {
        search,
        url,
        onRedirectOpened,
        callMethod = "http_get",
        parent = document.body,
    }: {
        search?: Record<string, string>;
        url?: string;
        onRedirectOpened?: (iFrame: HTMLIFrameElement) => void;
        callMethod?: "http_get" | "http_post";
        parent?: HTMLElement;
    }
) => {
    // Compute url with search params
    const urlParameters = Object.keys(search || {})
        .filter((key) => !!key && !!search[key])
        .map((key) => `${key}=${search[key]}`)
        .join("&");

    const iFrameUrl = `${url || `${window.location.origin}/redirect/index.html`}${
        urlParameters && callMethod === "http_get" ? `?${urlParameters}` : ""
    }`;

    // Redirect to the iFrame
    let form;
    if (callMethod === "http_get") {
        iFrame.setAttribute("src", iFrameUrl);
    } else if (callMethod === "http_post") {
        form = document.createElement("form");
        form.target = I_FRAME_NAME;
        form.action = iFrameUrl;
        form.method = "post";

        Object.keys(search).forEach((key) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = search[key];
            form.appendChild(input);
        });

        parent.appendChild(form);
        form.submit();
    }

    if (onRedirectOpened) {
        onRedirectOpened(iFrame);
    }

    return form;
};

export const setRedirectLocale = (iFrame: HTMLIFrameElement, locale: SupportedLocale) => {
    const onRedirectIFrameMessage = (event: MessageEvent) => {
        if (event.data.type === "REDIRECT_OPENED") {
            window.removeEventListener("message", onRedirectIFrameMessage);

            iFrame?.contentWindow?.postMessage({ type: "LOCALE_UPDATE", locale }, "*");
        }
    };

    window.addEventListener("message", onRedirectIFrameMessage);
};

export const postClientRedirectFailure = (error: Error) =>
    window.parent.postMessage({ type: "CLIENT_REDIRECT_FAILURE", error }, "*");

export const onRedirectFailure = (callback: (error: Error) => void) =>
    window.addEventListener("message", (event) => {
        if (event.data.type === "CLIENT_REDIRECT_FAILURE") {
            callback(event.data.error);
        }
    });

export const setThreeDsParameters = (
    iFrame: HTMLIFrameElement,
    issuerToken: { issuerToken: string; payload: Record<string, unknown> }
) => {
    const onThreeDsWrapperMessage = (event: MessageEvent) => {
        if (event.data.type === "THREE_DS_WRAPPER_READY") {
            window.removeEventListener("message", onThreeDsWrapperMessage);

            iFrame?.contentWindow?.postMessage(
                { type: "THREE_DS_PARAMETERS", issuerToken: issuerToken.issuerToken, payload: issuerToken.payload },
                "*"
            );
        }
    };

    window.addEventListener("message", onThreeDsWrapperMessage);
};

export const cleanup = (iFrame: HTMLElement, form?: HTMLElement) => {
    // Close iFrame and cleanup
    if (form) {
        form.remove();
    }

    iFrame.remove();
};

export const redirectInIFrame = async <T>({
    search = {},
    promise,
    url,
    callMethod = "http_get",
    iFrameSize,
    parent = document.body,
    onRedirectOpened,
    locale,
}: {
    search?: Record<string, string>;
    promise?: Promise<T>;
    locale: SupportedLocale;
    url?: string;
    onRedirectOpened?: (iFrame: HTMLIFrameElement) => void;
    callMethod?: "http_get" | "http_post";
    iFrameSize?: string | { width?: string; height?: string; top?: string; left?: string };
    parent?: HTMLElement;
}): Promise<T> => {
    const iFrame = createIFrame({ iFrameSize, parent });

    const form = redirect(iFrame, { search, url, callMethod, onRedirectOpened, parent });

    setRedirectLocale(iFrame, locale);

    if (promise) {
        // Wait for promise for redirect page return
        try {
            const response = await promise;
            cleanup(iFrame, form);

            return response;
        } catch (error) {
            cleanup(iFrame, form);
            throw error;
        }
    }

    return null;
};
